import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Box,
  TextField,
  Alert,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Stack,
  FormHelperText
} from '@mui/material';
import {
  Settings as SettingsIcon,
  List as ListIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Email as EmailIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import './CallSystemSettings.css';

const DepartmentSettings = ({ user, onClose }) => {
  // State for configuration
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Form state - departments with emails
  const [formData, setFormData] = useState({
    ClientID: '',
    Departments: [
      { name: 'Public Works', emails: [] },
      { name: 'Streets', emails: [] },
      { name: 'Water', emails: [] },
      { name: 'Parks', emails: [] },
      { name: 'Fire Department', emails: [] }
    ]
  });

  // Dialog states
  const [departmentDialog, setDepartmentDialog] = useState(false);
  const [newDepartment, setNewDepartment] = useState('');
  const [editingDepartment, setEditingDepartment] = useState(null);
  const [editDialog, setEditDialog] = useState(false);
  const [newEmail, setNewEmail] = useState('');

  // Email validation function - returns object with validation result
  const validateEmail = (email) => {
    // Basic format check
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return { valid: false, error: 'Invalid email format' };
    }
    
    // Check for common typos
    const trimmed = email.trim().toLowerCase();
    if (trimmed.includes('..') || trimmed.startsWith('.') || trimmed.endsWith('.')) {
      return { valid: false, error: 'Email contains invalid characters' };
    }
    
    // Check domain has valid TLD
    const parts = trimmed.split('@');
    if (parts.length !== 2) {
      return { valid: false, error: 'Invalid email format' };
    }
    
    const domain = parts[1];
    const tldRegex = /\.[a-z]{2,}$/i;
    if (!tldRegex.test(domain)) {
      return { valid: false, error: 'Invalid domain format' };
    }
    
    // Check for common invalid domains
    const invalidDomains = ['example.com', 'test.com', 'invalid.com'];
    if (invalidDomains.includes(domain)) {
      return { valid: false, error: 'Please use a valid email address' };
    }
    
    return { valid: true };
  };

  // Initialize form with user's ClientID
  useEffect(() => {
    if (user?.ClientID) {
      console.log('Setting ClientID in formData:', user.ClientID);
      setFormData(prev => ({ ...prev, ClientID: user.ClientID }));
      fetchConfig();
    } else {
      console.warn('User object or ClientID is missing:', user);
    }
  }, [user]);

  // Fetch existing configuration
  const fetchConfig = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(`/api/callSystem/callSystemConfig/${user.ClientID}`);
      
      if (response.ok) {
        const data = await response.json();
        setConfig(data.data);
        
        // Handle both old format (strings) and new format (objects with emails)
        let departments = data.data.Departments || [];
        if (departments.length > 0 && typeof departments[0] === 'string') {
          // Convert old format to new format
          departments = departments.map(dept => ({ name: dept, emails: [] }));
        }
        
        setFormData({
          ClientID: data.data.ClientID,
          Departments: departments.length > 0 ? departments : [
            { name: 'Public Works', emails: [] },
            { name: 'Streets', emails: [] },
            { name: 'Water', emails: [] },
            { name: 'Parks', emails: [] },
            { name: 'Fire Department', emails: [] }
          ]
        });
      } else if (response.status === 404) {
        // No configuration exists yet, use default departments
        setConfig(null);
      } else {
        throw new Error('Failed to load configuration');
      }
    } catch (err) {
      setError('Failed to load department configuration');
      console.error('Error fetching config:', err);
    } finally {
      setLoading(false);
    }
  };

  // Save configuration with specific data
  const saveConfigWithData = async (dataToUse = null) => {
    const currentFormData = dataToUse || formData;
    
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const method = config ? 'PUT' : 'POST';
      const url = config 
        ? `/api/callSystem/callSystemConfig/${user.ClientID}`
        : '/api/callSystem/callSystemConfig';

      // Only save departments, preserve other existing config if it exists
      const dataToSave = config ? {
        ...config,
        Departments: currentFormData.Departments
      } : {
        ClientID: currentFormData.ClientID,
        Departments: currentFormData.Departments,
        CallModuleEnabled: false // Disable AI call system
      };

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(dataToSave)
      });

      if (response.ok) {
        const data = await response.json();
        setConfig(data.data);
        setSuccess('Configuration saved automatically!');
        
        // Clear success message after 2 seconds
        setTimeout(() => setSuccess(null), 2000);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to save configuration');
      }
    } catch (err) {
      setError(err.message || 'Failed to save department configuration');
      console.error('Error saving config:', err);
    } finally {
      setSaving(false);
    }
  };

  // Save configuration (legacy function for compatibility)
  const saveConfig = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      // Debug logging
      console.log('User object:', user);
      console.log('Form data being sent:', formData);
      console.log('ClientID in formData:', formData.ClientID);
      console.log('ClientID in user:', user?.ClientID);

      const method = config ? 'PUT' : 'POST';
      const url = config 
        ? `/api/callSystem/callSystemConfig/${user.ClientID}`
        : '/api/callSystem/callSystemConfig';

      // Only save departments, preserve other existing config if it exists
      const dataToSave = config ? {
        ...config,
        Departments: formData.Departments
      } : {
        ClientID: formData.ClientID,
        Departments: formData.Departments,
        CallModuleEnabled: false // Disable AI call system
      };

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(dataToSave)
      });

      if (response.ok) {
        const data = await response.json();
        setConfig(data.data);
        setSuccess('Department configuration saved successfully!');
        
        // Clear success message after 3 seconds
        setTimeout(() => setSuccess(null), 3000);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to save configuration');
      }
    } catch (err) {
      setError(err.message || 'Failed to save department configuration');
      console.error('Error saving config:', err);
    } finally {
      setSaving(false);
    }
  };

  // Department management functions
  const addDepartment = async () => {
    if (newDepartment.trim() && !formData.Departments.some(d => d.name === newDepartment.trim())) {
      const updatedFormData = {
        ...formData,
        Departments: [...formData.Departments, { name: newDepartment.trim(), emails: [] }]
      };
      
      setFormData(updatedFormData);
      setNewDepartment('');
      setDepartmentDialog(false);
      
      // Auto-save after adding department
      await saveConfigWithData(updatedFormData);
    }
  };

  const removeDepartment = async (departmentName) => {
    const updatedFormData = {
      ...formData,
      Departments: formData.Departments.filter(d => d.name !== departmentName)
    };
    
    setFormData(updatedFormData);
    
    // Auto-save after removing department
    await saveConfigWithData(updatedFormData);
  };

  const editDepartment = (department) => {
    setEditingDepartment(department);
    setEditDialog(true);
  };

  const addEmailToDepartment = async () => {
    if (newEmail.trim()) {
      const validation = validateEmail(newEmail.trim());
      
      if (!validation.valid) {
        setError(validation.error || 'Invalid email address');
        return;
      }
      
      const trimmedEmail = newEmail.trim().toLowerCase();
      
      // Check if email already exists in this department (case-insensitive)
      if (editingDepartment.emails.some(e => e.toLowerCase() === trimmedEmail)) {
        setError('This email is already added to this department');
        return;
      }
      
      // Update formData
      const updatedFormData = {
        ...formData,
        Departments: formData.Departments.map(dept => 
          dept.name === editingDepartment.name 
            ? { ...dept, emails: [...dept.emails, trimmedEmail] }
            : dept
        )
      };
      
      setFormData(updatedFormData);
      
      // Also update editingDepartment state to reflect the change immediately
      setEditingDepartment(prev => ({
        ...prev,
        emails: [...prev.emails, trimmedEmail]
      }));
      
      setNewEmail('');
      setError(null); // Clear any previous errors
      
      // Auto-save after adding email
      await saveConfigWithData(updatedFormData);
    }
  };

  const removeEmailFromDepartment = async (departmentName, emailToRemove) => {
    // Update formData
    const updatedFormData = {
      ...formData,
      Departments: formData.Departments.map(dept => 
        dept.name === departmentName 
          ? { ...dept, emails: dept.emails.filter(email => email !== emailToRemove) }
          : dept
      )
    };
    
    setFormData(updatedFormData);
    
    // Also update editingDepartment state if it's the department being edited
    if (editingDepartment && editingDepartment.name === departmentName) {
      setEditingDepartment(prev => ({
        ...prev,
        emails: prev.emails.filter(email => email !== emailToRemove)
      }));
    }
    
    // Auto-save after removing email
    await saveConfigWithData(updatedFormData);
  };

  const isNewConfig = !config;

  return (
    <Card>
      <CardContent>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h5" component="h2">
            <SettingsIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Department Configuration
          </Typography>
          <Button onClick={onClose} variant="outlined">
            Close
          </Button>
        </Box>

        {/* First-time Setup Guide */}
        {isNewConfig && (
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              📋 Department Setup
            </Typography>
            <Typography variant="body2" paragraph>
              Configure the departments that citizens can report issues to. Add email addresses for each department to receive notifications when calls are assigned.
            </Typography>
          </Alert>
        )}

        {/* Error/Success Messages */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        {/* Loading State */}
        {loading && (
          <Box display="flex" justifyContent="center" my={3}>
            <CircularProgress />
          </Box>
        )}

        {/* Department Configuration */}
        {!loading && (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                <ListIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Departments & Email Notifications
              </Typography>
              
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Configure departments and their email addresses. Department emails will receive notifications when calls are assigned or reassigned.
              </Typography>
              
              {formData.Departments.length === 0 && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    No departments configured. Add departments that citizens can report issues to.
                  </Typography>
                </Alert>
              )}
              
              <List dense>
                {formData.Departments.map((dept, index) => (
                  <ListItem key={index} sx={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
                      <ListItemText 
                        primary={dept.name}
                        secondary={
                          dept.emails.length > 0 
                            ? `${dept.emails.length} email${dept.emails.length > 1 ? 's' : ''} configured`
                            : 'No emails configured'
                        }
                      />
                      <ListItemSecondaryAction>
                        <IconButton edge="end" onClick={() => editDepartment(dept)} sx={{ mr: 1 }}>
                          <EditIcon />
                        </IconButton>
                        <IconButton edge="end" onClick={() => removeDepartment(dept.name)}>
                          <DeleteIcon />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </Box>
                    
                    {/* Email chips */}
                    {dept.emails.length > 0 && (
                      <Box sx={{ mt: 1, width: '100%' }}>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                          {dept.emails.map((email, emailIndex) => (
                            <Chip
                              key={emailIndex}
                              label={email}
                              size="small"
                              onDelete={() => removeEmailFromDepartment(dept.name, email)}
                              color="primary"
                              variant="outlined"
                            />
                          ))}
                        </Stack>
                      </Box>
                    )}
                  </ListItem>
                ))}
              </List>
              
              <Button
                startIcon={<AddIcon />}
                onClick={() => setDepartmentDialog(true)}
                variant="outlined"
                size="small"
                sx={{ mt: 1 }}
              >
                Add Department
              </Button>
            </Grid>
          </Grid>
        )}

        <Divider sx={{ my: 3 }} />

        {/* Auto-save Status */}
        {saving && (
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <CircularProgress size={16} />
            <Typography variant="body2" color="textSecondary">
              Saving changes...
            </Typography>
          </Box>
        )}

        {/* Quick Setup Tips */}
        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="body2">
            <strong>Department Setup Tips:</strong>
          </Typography>
          <Typography variant="body2" component="div" sx={{ mt: 1 }}>
            • Add common departments like "Public Works", "Streets", "Water", "Parks"
          </Typography>
          <Typography variant="body2" component="div">
            • Add email addresses for each department to receive call notifications
          </Typography>
          <Typography variant="body2" component="div">
            • You can add multiple email addresses per department
          </Typography>
          <Typography variant="body2" component="div">
            • Changes are saved automatically - no need to click a save button!
          </Typography>
          <Typography variant="body2" component="div">
            • Departments will be available in call tracking forms and reports
          </Typography>
        </Alert>

        {/* Department Dialog */}
        <Dialog open={departmentDialog} onClose={() => setDepartmentDialog(false)}>
          <DialogTitle>Add Department</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Department Name"
              fullWidth
              value={newDepartment}
              onChange={(e) => setNewDepartment(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && addDepartment()}
              helperText="Enter the name of the department"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDepartmentDialog(false)}>Cancel</Button>
            <Button 
              onClick={addDepartment} 
              variant="contained"
              disabled={!newDepartment.trim() || formData.Departments.some(d => d.name === newDepartment.trim())}
            >
              Add
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit Department Dialog */}
        <Dialog open={editDialog} onClose={() => setEditDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>
            <Box display="flex" alignItems="center" gap={1}>
              <EmailIcon />
              Configure Emails for {editingDepartment?.name}
            </Box>
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Add email addresses that will receive notifications when calls are assigned to this department.
            </Typography>
            
            {/* Current emails */}
            {editingDepartment?.emails && editingDepartment.emails.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Current Email Addresses:
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {editingDepartment.emails.map((email, index) => (
                    <Chip
                      key={index}
                      label={email}
                      size="small"
                      onDelete={() => removeEmailFromDepartment(editingDepartment.name, email)}
                      color="primary"
                    />
                  ))}
                </Stack>
              </Box>
            )}
            
            {/* Add new email */}
            <Box display="flex" gap={1} alignItems="flex-start">
              <TextField
                margin="dense"
                label="Email Address"
                type="email"
                fullWidth
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && addEmailToDepartment()}
                helperText="Enter a valid email address"
                error={newEmail && !validateEmail(newEmail)}
              />
              <Button
                onClick={addEmailToDepartment}
                variant="contained"
                disabled={!newEmail.trim() || !validateEmail(newEmail.trim())}
                sx={{ mt: 1 }}
              >
                Add
              </Button>
            </Box>
            
            {newEmail && !validateEmail(newEmail) && (
              <FormHelperText error>
                Please enter a valid email address
              </FormHelperText>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditDialog(false)}>Done</Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default DepartmentSettings; 